import { useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { CardHeader, NoDataFound, ConversionBadge, stageColor, pctOfLeads } from "./Common";

// MUI DataGrid theming kept minimal and scoped to this table via `sx`, so it
// sits inside the same white-card/Tailwind look as the rest of the dashboard
// instead of pulling in MUI's default chrome.
const gridSx = {
  border: "none",
  fontSize: "0.75rem",
  color: "#374151",
  "& .MuiDataGrid-columnHeaders": {
    borderBottom: "1px solid #f3f4f6",
    backgroundColor: "transparent",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 500,
    fontSize: "0.7rem",
    color: "#9ca3af",
    textTransform: "none",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #f9fafb",
  },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
  "& .MuiDataGrid-row:hover": { backgroundColor: "rgba(249, 250, 251, 0.6)" },
  "& .MuiDataGrid-footerContainer": { borderTop: "1px solid #f3f4f6" },
  "& .MuiDataGrid-virtualScroller": { minHeight: 80 },
};

export default function StagesTable({ stages }) {
  // Leads (the first stage) is the fixed 100% baseline. Every row's
  // "% of leads" is that stage's total measured against leads -- the same
  // relation used in the KPI row, funnel cards, and donut, so this table
  // agrees with the rest of the dashboard instead of showing a different
  // (stage-over-previous-stage) number.
  const leadsTotal = stages?.[0]?.total || 0;

  const rows = useMemo(
    () =>
      (stages || []).map((s, i) => ({
        id: s.stage_id,
        index: i,
        stage_name: s.stage_name,
        total: s.total,
        pct_of_leads: pctOfLeads(s.total, leadsTotal),
        isFirst: i === 0,
      })),
    [stages, leadsTotal],
  );

  const columns = [
    {
      field: "stage_name",
      headerName: "Stage",
      flex: 1.4,
      minWidth: 160,
      renderCell: (params) => (
        <div className="flex items-center gap-2 h-full">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: stageColor(params.row.index) }} />
          <span className="text-gray-700">{params.value}</span>
        </div>
      ),
    },
    {
      field: "total",
      headerName: "Addresses",
      flex: 0.8,
      minWidth: 100,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => <span className="text-gray-700 font-medium">{params.value}</span>,
    },
    {
      field: "pct_of_leads",
      headerName: "% of total leads",
      flex: 0.9,
      minWidth: 130,
      type: "number",
      align: "right",
      headerAlign: "right",
      description: "Share of total leads (the entry stage) that reached this stage",
      renderCell: (params) =>
        params.row.isFirst ? (
          <div className="w-full item-center justify-center">
            <span className="text-gray-300">100%</span>
          </div>
        ) : (
          <div className="w-full item-center justify-center py-3.5">
            <ConversionBadge pct={params.value} />
          </div>
        ),
    },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col mb-4">
      <CardHeader title="Stages breakdown" subtitle="Each stage's addresses as a % of total leads (the entry stage)" />

      {!stages?.length ? (
        <NoDataFound label="No pipeline stages yet" />
      ) : (
        <Box sx={{ width: "100%" }}>
          <DataGrid rows={rows} columns={columns} hideFooter={rows.length <= 10} pageSizeOptions={[10, 25]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick disableColumnMenu autoHeight sx={gridSx} />
        </Box>
      )}
    </div>
  );
}
